/**
 * AudioService — 100% IP-Free / Royalty-Free Procedural Audio System for Bá Chủ Vỉa Hè 2.0
 * 
 * Features:
 * - Independent BGM (Background Music) and SFX (Sound Effects) controls:
 *   - Separate ON/OFF toggles
 *   - Separate Volume Sliders (0% - 100%)
 * - Pure Web Audio API synthesis (0KB asset download, zero copyright/DMCA risk)
 * - SFX: ting_ting (sales), tap (UI), siren (urban raid), upgrade (chime), celebration (fanfare)
 * - Procedural BGM: Gentle Vietnamese pentatonic lo-fi street chill, shifts dynamically to night ambient
 * - Mobile Autoplay Policy compliance: unlocks audio context on first user gesture
 * - Persistent settings in localStorage
 */

export type SoundEffectType = 'coin' | 'tap' | 'siren' | 'upgrade' | 'celebration';

export class AudioService {
  private static instance: AudioService | null = null;

  private audioCtx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private bgmGain: GainNode | null = null;

  // Independent BGM state
  private isBgmEnabled = true;
  private bgmVolume = 0.6; // 0.0 to 1.0 (internal master scale ~0.18)

  // Independent SFX state
  private isSfxEnabled = true;
  private sfxVolume = 0.8; // 0.0 to 1.0 (internal master scale ~0.5)

  private isBgmPlaying = false;
  private bgmTimer: number | null = null;
  private isNightMode = false;
  private lastCoinTime = 0;

  // LocalStorage keys
  private static readonly KEY_BGM_ENABLED = 'tradaviahe_bgm_enabled';
  private static readonly KEY_BGM_VOLUME = 'tradaviahe_bgm_volume';
  private static readonly KEY_SFX_ENABLED = 'tradaviahe_sfx_enabled';
  private static readonly KEY_SFX_VOLUME = 'tradaviahe_sfx_volume';

  private constructor() {
    this.loadSettings();
  }

  public static getInstance(): AudioService {
    if (!AudioService.instance) {
      AudioService.instance = new AudioService();
    }
    return AudioService.instance;
  }

  private loadSettings(): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const savedBgm = localStorage.getItem(AudioService.KEY_BGM_ENABLED);
        if (savedBgm !== null) this.isBgmEnabled = savedBgm === 'true';

        const savedBgmVol = localStorage.getItem(AudioService.KEY_BGM_VOLUME);
        if (savedBgmVol !== null) this.bgmVolume = Math.max(0, Math.min(1, parseFloat(savedBgmVol)));

        const savedSfx = localStorage.getItem(AudioService.KEY_SFX_ENABLED);
        if (savedSfx !== null) this.isSfxEnabled = savedSfx === 'true';

        const savedSfxVol = localStorage.getItem(AudioService.KEY_SFX_VOLUME);
        if (savedSfxVol !== null) this.sfxVolume = Math.max(0, Math.min(1, parseFloat(savedSfxVol)));
      }
    } catch {
      this.isBgmEnabled = true;
      this.bgmVolume = 0.6;
      this.isSfxEnabled = true;
      this.sfxVolume = 0.8;
    }
  }

  private saveSettings(): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(AudioService.KEY_BGM_ENABLED, String(this.isBgmEnabled));
        localStorage.setItem(AudioService.KEY_BGM_VOLUME, String(this.bgmVolume));
        localStorage.setItem(AudioService.KEY_SFX_ENABLED, String(this.isSfxEnabled));
        localStorage.setItem(AudioService.KEY_SFX_VOLUME, String(this.sfxVolume));
      }
    } catch {
      // ignore
    }
  }

  /**
   * Initializes AudioContext and binds unlock listener for mobile browser autoplay policy
   */
  public init(): void {
    if (typeof window === 'undefined') return;

    const unlock = (): void => {
      this.ensureContext();
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }
      if (this.isBgmEnabled && !this.isBgmPlaying) {
        this.startBgm();
      }
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };

    window.addEventListener('pointerdown', unlock, { once: true, passive: true });
    window.addEventListener('keydown', unlock, { once: true, passive: true });
    window.addEventListener('touchstart', unlock, { once: true, passive: true });
  }

  private ensureContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;

    if (!this.audioCtx) {
      const AudioCtxClass =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtxClass) return null;

      this.audioCtx = new AudioCtxClass();

      // Master Gain
      this.masterGain = this.audioCtx.createGain();
      this.masterGain.gain.setValueAtTime(1.0, this.audioCtx.currentTime);
      this.masterGain.connect(this.audioCtx.destination);

      // SFX Gain node
      this.sfxGain = this.audioCtx.createGain();
      const initialSfxGain = this.isSfxEnabled ? this.sfxVolume * 0.5 : 0.0;
      this.sfxGain.gain.setValueAtTime(initialSfxGain, this.audioCtx.currentTime);
      this.sfxGain.connect(this.masterGain);

      // BGM Gain node
      this.bgmGain = this.audioCtx.createGain();
      const baseScale = this.isNightMode ? 0.22 : 0.18;
      const initialBgmGain = this.isBgmEnabled ? this.bgmVolume * baseScale : 0.0;
      this.bgmGain.gain.setValueAtTime(initialBgmGain, this.audioCtx.currentTime);
      this.bgmGain.connect(this.masterGain);
    }

    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => {});
    }

    return this.audioCtx;
  }

  // ==========================================
  // BGM CONTROLS
  // ==========================================

  public isBgmOn(): boolean {
    return this.isBgmEnabled;
  }

  public getBgmVolume(): number {
    return this.bgmVolume;
  }

  public toggleBgm(): boolean {
    return this.setBgmEnabled(!this.isBgmEnabled);
  }

  public setBgmEnabled(enabled: boolean): boolean {
    this.isBgmEnabled = enabled;
    this.saveSettings();

    const ctx = this.ensureContext();
    if (ctx && this.bgmGain) {
      const baseScale = this.isNightMode ? 0.22 : 0.18;
      const target = this.isBgmEnabled ? this.bgmVolume * baseScale : 0.0;
      this.bgmGain.gain.setValueAtTime(this.bgmGain.gain.value, ctx.currentTime);
      this.bgmGain.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.15);
    }

    if (this.isBgmEnabled) {
      if (!this.isBgmPlaying) {
        this.startBgm();
      }
    } else {
      this.stopBgm();
    }

    return this.isBgmEnabled;
  }

  public setBgmVolume(val: number): void {
    const clamped = Math.max(0, Math.min(1, val));
    this.bgmVolume = clamped;
    this.saveSettings();

    const ctx = this.ensureContext();
    if (ctx && this.bgmGain && this.isBgmEnabled) {
      const baseScale = this.isNightMode ? 0.22 : 0.18;
      const target = this.bgmVolume * baseScale;
      this.bgmGain.gain.setValueAtTime(this.bgmGain.gain.value, ctx.currentTime);
      this.bgmGain.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.05);
    }
  }

  // ==========================================
  // SFX CONTROLS
  // ==========================================

  public isSfxOn(): boolean {
    return this.isSfxEnabled;
  }

  public getSfxVolume(): number {
    return this.sfxVolume;
  }

  public toggleSfx(): boolean {
    return this.setSfxEnabled(!this.isSfxEnabled);
  }

  public setSfxEnabled(enabled: boolean): boolean {
    this.isSfxEnabled = enabled;
    this.saveSettings();

    const ctx = this.ensureContext();
    if (ctx && this.sfxGain) {
      const target = this.isSfxEnabled ? this.sfxVolume * 0.5 : 0.0;
      this.sfxGain.gain.setValueAtTime(this.sfxGain.gain.value, ctx.currentTime);
      this.sfxGain.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.08);
    }

    if (this.isSfxEnabled) {
      this.playTap();
    }

    return this.isSfxEnabled;
  }

  public setSfxVolume(val: number): void {
    const clamped = Math.max(0, Math.min(1, val));
    this.sfxVolume = clamped;
    this.saveSettings();

    const ctx = this.ensureContext();
    if (ctx && this.sfxGain && this.isSfxEnabled) {
      const target = this.sfxVolume * 0.5;
      this.sfxGain.gain.setValueAtTime(this.sfxGain.gain.value, ctx.currentTime);
      this.sfxGain.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.05);
    }
  }

  // ==========================================
  // MASTER / QUICK TOGGLES
  // ==========================================

  public isAnyEnabled(): boolean {
    return this.isBgmEnabled || this.isSfxEnabled;
  }

  public isEnabled(): boolean {
    return this.isAnyEnabled();
  }

  public toggle(): boolean {
    return this.toggleMaster();
  }

  /**
   * Toggles master audio: if any is on, turn both off. If both off, turn both on.
   */
  public toggleMaster(): boolean {
    const newState = !this.isAnyEnabled();
    this.setBgmEnabled(newState);
    this.setSfxEnabled(newState);
    return newState;
  }

  /**
   * Returns current HUD icon based on independent audio states
   */
  public getHudIcon(): string {
    if (this.isBgmEnabled && this.isSfxEnabled) return '🔊';
    if (this.isBgmEnabled) return '🎵';
    if (this.isSfxEnabled) return '🔔';
    return '🔇';
  }

  public setNightMode(isNight: boolean): void {
    if (this.isNightMode === isNight) return;
    this.isNightMode = isNight;
    if (this.isBgmEnabled && this.isBgmPlaying) {
      const ctx = this.ensureContext();
      if (ctx && this.bgmGain) {
        const baseScale = isNight ? 0.22 : 0.18;
        const targetVol = this.bgmVolume * baseScale;
        this.bgmGain.gain.setTargetAtTime(targetVol, ctx.currentTime, 0.5);
      }
    }
  }

  public pauseMusic(): void {
    if (this.audioCtx && this.audioCtx.state === 'running') {
      this.audioCtx.suspend().catch(() => {});
    }
  }

  public resumeMusic(): void {
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => {});
    }
  }

  // ==========================================
  // PROCEDURAL SOUND EFFECTS (SYNTHESIZED)
  // ==========================================

  /**
   * Ting-ting cash chime on item sold (1400Hz -> 2800Hz with fast bell decay)
   */
  public playSale(): void {
    if (!this.isSfxEnabled || this.sfxVolume <= 0) return;
    const now = Date.now();
    // Throttle multiple rapid sales to avoid audio clipping
    if (now - this.lastCoinTime < 80) return;
    this.lastCoinTime = now;

    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;

    const t = ctx.currentTime;

    // First bell tone (1400Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(1400, t);
    osc1.frequency.exponentialRampToValueAtTime(2800, t + 0.08);

    gain1.gain.setValueAtTime(0.6, t);
    gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.25);

    osc1.connect(gain1);
    gain1.connect(this.sfxGain);

    osc1.start(t);
    osc1.stop(t + 0.26);

    // Second resonance harmonic (3200Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(3200, t + 0.05);

    gain2.gain.setValueAtTime(0, t);
    gain2.gain.setValueAtTime(0.4, t + 0.05);
    gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.35);

    osc2.connect(gain2);
    gain2.connect(this.sfxGain);

    osc2.start(t + 0.05);
    osc2.stop(t + 0.36);
  }

  /**
   * Crisp UI tap / button click
   */
  public playTap(): void {
    if (!this.isSfxEnabled || this.sfxVolume <= 0) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;

    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(450, t);
    osc.frequency.exponentialRampToValueAtTime(120, t + 0.04);

    gain.gain.setValueAtTime(0.3, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);

    osc.connect(gain);
    gain.connect(this.sfxGain);

    osc.start(t);
    osc.stop(t + 0.05);
  }

  /**
   * Ascending arpeggio chime when buying upgrades (C5 -> E5 -> G5)
   */
  public playUpgrade(): void {
    if (!this.isSfxEnabled || this.sfxVolume <= 0) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;

    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    const startTime = ctx.currentTime;

    notes.forEach((freq, idx) => {
      const t = startTime + idx * 0.07;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, t);

      gain.gain.setValueAtTime(0.35, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.28);

      osc.connect(gain);
      gain.connect(this.sfxGain!);

      osc.start(t);
      osc.stop(t + 0.29);
    });
  }

  /**
   * Alert siren / inspection warning (wobbling frequency 750Hz - 1100Hz)
   */
  public playAlert(): void {
    if (!this.isSfxEnabled || this.sfxVolume <= 0) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;

    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(750, t);
    osc.frequency.linearRampToValueAtTime(1100, t + 0.2);
    osc.frequency.linearRampToValueAtTime(750, t + 0.4);
    osc.frequency.linearRampToValueAtTime(1100, t + 0.6);
    osc.frequency.linearRampToValueAtTime(750, t + 0.8);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1500, t);

    gain.gain.setValueAtTime(0.25, t);
    gain.gain.linearRampToValueAtTime(0.25, t + 0.7);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.9);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGain);

    osc.start(t);
    osc.stop(t + 0.95);
  }

  /**
   * Victory celebration fanfare (multivoice chord progression)
   */
  public playCelebration(): void {
    if (!this.isSfxEnabled || this.sfxVolume <= 0) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxGain) return;

    const chords = [
      [523.25, 659.25, 783.99],          // C major
      [587.33, 698.46, 880.00],          // D minor
      [659.25, 783.99, 987.77],          // E minor
      [783.99, 987.77, 1174.66, 1567.98] // G major triumphant
    ];

    const t0 = ctx.currentTime;
    chords.forEach((chord, cIdx) => {
      const stepTime = t0 + cIdx * 0.16;
      chord.forEach((freq) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = cIdx === chords.length - 1 ? 'triangle' : 'sine';
        osc.frequency.setValueAtTime(freq, stepTime);

        gain.gain.setValueAtTime(0.2, stepTime);
        gain.gain.exponentialRampToValueAtTime(0.001, stepTime + (cIdx === chords.length - 1 ? 0.8 : 0.22));

        osc.connect(gain);
        gain.connect(this.sfxGain!);

        osc.start(stepTime);
        osc.stop(stepTime + 0.85);
      });
    });
  }

  // ==========================================
  // PROCEDURAL BGM (VIETNAMESE PENTATONIC LO-FI)
  // ==========================================

  public startBgm(): void {
    if (this.isBgmPlaying || !this.isBgmEnabled) return;
    const ctx = this.ensureContext();
    if (!ctx) return;

    this.isBgmPlaying = true;
    this.scheduleNextBgmPhrase();
  }

  public stopBgm(): void {
    this.isBgmPlaying = false;
    if (this.bgmTimer !== null) {
      window.clearTimeout(this.bgmTimer);
      this.bgmTimer = null;
    }
  }

  /**
   * Generates continuous relaxing Vietnamese street pentatonic lo-fi phrases
   * Notes: C4, D4, E4, G4, A4, C5, D5, E5, G5
   */
  private scheduleNextBgmPhrase(): void {
    if (!this.isBgmPlaying || !this.isBgmEnabled) return;

    const ctx = this.ensureContext();
    if (!ctx || !this.bgmGain) return;

    // Pentatonic scale frequencies (C, D, E, G, A)
    const scale = [261.63, 293.66, 329.63, 392.00, 440.00, 523.25, 587.33, 659.25, 783.99];
    const phraseLen = this.isNightMode ? 6 : 8;
    const stepDuration = this.isNightMode ? 0.45 : 0.35; // Night is slower, day is brisk
    const t0 = ctx.currentTime + 0.05;

    // Gentle bass root note
    const bassNote = this.isNightMode ? 130.81 : 164.81; // C3 or E3
    const bassOsc = ctx.createOscillator();
    const bassGain = ctx.createGain();
    const bassFilter = ctx.createBiquadFilter();

    bassOsc.type = 'sine';
    bassOsc.frequency.setValueAtTime(bassNote, t0);
    bassFilter.type = 'lowpass';
    bassFilter.frequency.setValueAtTime(250, t0);

    bassGain.gain.setValueAtTime(0.2, t0);
    bassGain.gain.linearRampToValueAtTime(0.001, t0 + phraseLen * stepDuration);

    bassOsc.connect(bassFilter);
    bassFilter.connect(bassGain);
    bassGain.connect(this.bgmGain);

    bassOsc.start(t0);
    bassOsc.stop(t0 + phraseLen * stepDuration + 0.05);

    // Melody notes
    for (let i = 0; i < phraseLen; i++) {
      // 70% chance to play a note, 30% rest for natural breath
      if (Math.random() > 0.3) {
        const noteIdx = Math.floor(Math.random() * scale.length);
        const freq = scale[noteIdx] ?? 392.0;
        const noteTime = t0 + i * stepDuration;

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();

        osc.type = this.isNightMode ? 'sine' : 'triangle';
        osc.frequency.setValueAtTime(freq, noteTime);

        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(this.isNightMode ? 600 : 1200, noteTime);

        gain.gain.setValueAtTime(0.12, noteTime);
        gain.gain.exponentialRampToValueAtTime(0.0001, noteTime + stepDuration * 0.95);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(this.bgmGain);

        osc.start(noteTime);
        osc.stop(noteTime + stepDuration);
      }
    }

    // Schedule next phrase loop
    const phraseMs = (phraseLen * stepDuration + (this.isNightMode ? 1.0 : 0.5)) * 1000;
    this.bgmTimer = window.setTimeout(() => {
      this.scheduleNextBgmPhrase();
    }, phraseMs);
  }
}
